import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Document } from '@/shared/api/transcriptor/transcriptorApi';

import { TranscriptorConsultation } from '@/entities/transcriptor';

import { dentalConsultationMessages } from '../../config';
import {
  isDocumentFailedWithoutContent,
  isDocumentGenerating,
  parseEvaluationData,
} from '../../lib';
import { ConsultationEmptyState } from '../ConsultationEmptyState/ConsultationEmptyState';
import { ConsultationWidget } from '../ConsultationWidget/ConsultationWidget';

import styles from './EvaluationWidget.module.scss';

type EvaluationWidgetProps = {
  consultation?: TranscriptorConsultation | null;
  evaluation?: Document | null;
  onRegenerate?: () => void;
};

export const EvaluationWidget: FC<EvaluationWidgetProps> = ({
  consultation,
  evaluation,
  onRegenerate,
}) => {
  const status = consultation?.status;
  const documentStatus = evaluation?.status;

  if (
    documentStatus === 'skipped' ||
    isDocumentGenerating(evaluation) ||
    isDocumentFailedWithoutContent(evaluation)
  ) {
    return (
      <ConsultationEmptyState
        status={status}
        documentStatus={documentStatus}
        documentError={evaluation?.error}
        noDataMessage={dentalConsultationMessages.noEvaluationAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  if (!evaluation) {
    return (
      <ConsultationEmptyState
        status={status}
        noDataMessage={dentalConsultationMessages.noEvaluationAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  const data = parseEvaluationData(evaluation);

  if (data.length === 0) {
    return (
      <div className={styles.rawDataContainer}>
        <h3 className={cn('h5', styles.rawDataTitle)}>
          <FormattedMessage {...dentalConsultationMessages.evaluationDataRaw} />
        </h3>
        <pre className={styles.rawDataContent}>
          {JSON.stringify(evaluation, null, 2)}
        </pre>
      </div>
    );
  }

  return <ConsultationWidget data={data} showScores={true} />;
};
